import { useTranslation } from 'react-i18next';
import type { Profile } from '@/auth/OperatorProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { formatDateTime } from '@/lib/intl';

type RoleAssignment = Profile['roleAssignments'][number];

/**
 * Read-only rendering of the caller's own active Role assignments (FR-SP-2), unioned across
 * every one of the caller's own eligible organizations and deduped server-side (SL-0022,
 * Resolution 3) — this component only renders the array `GET /v1/profile` already returns; it
 * issues no fetch of its own (Resolution 5). No control here grants, revokes, or otherwise edits
 * a Role assignment (AC-PA-9's UI-observable half) — unlike the operator admin section's own
 * `RolesSection` (`src/admin/organizations/RolesSection.tsx`), which does expose grant/revoke,
 * this is strictly a read-only table.
 */
export function RoleAssignmentsSection({
  roleAssignments,
}: {
  roleAssignments: readonly RoleAssignment[];
}) {
  const { t } = useTranslation('profile');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="card-label">{t('roleAssignments.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={[
            {
              id: 'roleId',
              header: t('roleAssignments.roleIdHeader'),
              cell: ({ row }: { row: { original: RoleAssignment } }) => (
                <code>{row.original.roleId}</code>
              ),
            },
            {
              id: 'scopeType',
              header: t('roleAssignments.scopeHeader'),
              cell: ({ row }: { row: { original: RoleAssignment } }) => row.original.scopeType,
            },
            {
              id: 'scopeId',
              header: t('roleAssignments.scopeIdHeader'),
              cell: ({ row }: { row: { original: RoleAssignment } }) =>
                row.original.scopeId === null ? (
                  <span className="text-muted-foreground">{t('roleAssignments.globalScope')}</span>
                ) : (
                  <code>{row.original.scopeId}</code>
                ),
            },
            {
              id: 'grantedAt',
              header: t('roleAssignments.grantedAtHeader'),
              cell: ({ row }: { row: { original: RoleAssignment } }) =>
                formatDateTime(row.original.grantedAt),
            },
          ]}
          data={[...roleAssignments]}
          emptyMessage={t('roleAssignments.emptyMessage')}
        />
      </CardContent>
    </Card>
  );
}
